import { useEffect, useRef, useState } from 'react';
import '../styles.css';
import Spinner from '../components/Spinner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getMeta, ServiceInfo } from '../api/meta';
import { Link } from 'react-router-dom';

export default function PublicDocs() {
  const bgUrl = (import.meta.env.VITE_WEATHER_BG_URL as string) || '/images/rain.gif';
  const videoUrl = (import.meta.env.VITE_WEATHER_BG_VIDEO as string) || '';
  const videoRate = Number((import.meta.env.VITE_WEATHER_BG_RATE as string) || '0.25') || 0.25;
  const vRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = vRef.current; if (!v) return;
    try { v.playbackRate = videoRate; } catch {}
  }, [videoRate]);
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Feature tiles are static (no animation)
  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const meta = await getMeta();
        setServices(meta.services || []);
      } catch (e: any) { setError(e?.message || String(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <div>
      <Header showPortalLink={false} loginLabel="Developer Portal" />
      {videoUrl
        ? <video ref={vRef} className="weather-video" src={videoUrl} autoPlay muted loop playsInline aria-hidden="true" />
        : <div className={`weather-bg ${bgUrl ? 'img' : ''}`} style={bgUrl ? { ['--weather-bg-url' as any]: `url('${bgUrl}')`, backgroundImage: `var(--weather-bg-url)` } : undefined} aria-hidden="true" />}
      <div className="content docs-content" style={{ paddingTop: 24 }}>
        <div className="hero">
          <div className="hero-text">
            <h1>AfricaWeather Developer APIs</h1>
            <div className="muted">Build weather‑powered experiences using reliable, scalable APIs.</div>
            {/* CTA links removed; header provides navigation */}
          </div>
        </div>
        <div className="card" style={{ marginBottom: 12, textAlign: 'center', paddingTop: 28, paddingBottom: 28 }}>
          <h2 style={{ marginTop: 0 }}>Available Services</h2>
          <div className="lead" style={{ marginTop: 8, marginLeft: 'auto', marginRight: 'auto', maxWidth: 840 }}>
            Explore AfricaWeather’s REST APIs with live, production‑ready specs. You’ll find endpoints, parameters, examples, and status codes for the key weather services. Use this to evaluate integrations, prototype quickly, and understand data models before requesting access.
          </div>
        </div>
        {/* Bold feature grid below the card */}
        <div className="features features-row">
          <div className="feature a">
            <div className="feature-icon" style={{ background: '#93c5fd' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 21l-4.35-4.35" stroke="#0b1220" strokeWidth="2" strokeLinecap="round"/><circle cx="10" cy="10" r="6" stroke="#0b1220" strokeWidth="2"/></svg>
            </div>
            <div>
              <div className="feature-title">Discover endpoints</div>
              <div className="feature-copy">Find required parameters and query patterns quickly.</div>
            </div>
          </div>
          <div className="feature b">
            <div className="feature-icon" style={{ background: '#67e8f9' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 4L3 12l4 8" stroke="#0b1220" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M17 4l4 8-4 8" stroke="#0b1220" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div>
              <div className="feature-title">Understand schemas</div>
              <div className="feature-copy">Clear response models and weather code meanings.</div>
            </div>
          </div>
          <div className="feature c">
            <div className="feature-icon" style={{ background: '#86efac' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 3v12" stroke="#0b1220" strokeWidth="2" strokeLinecap="round"/><path d="M8 11l4 4 4-4" stroke="#0b1220" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 21h16" stroke="#0b1220" strokeWidth="2" strokeLinecap="round"/></svg>
            </div>
            <div>
              <div className="feature-title">Download & test</div>
              <div className="feature-copy">Export OpenAPI and Postman collections in one click.</div>
            </div>
          </div>
          <div className="feature d">
            <div className="feature-icon" style={{ background: '#fde68a' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 4l6 6-6 6-6-6 6-6z" stroke="#0b1220" strokeWidth="2"/><path d="M4 20l4-4" stroke="#0b1220" strokeWidth="2" strokeLinecap="round"/></svg>
            </div>
            <div>
              <div className="feature-title">Scale with confidence</div>
              <div className="feature-copy">Plan usage with rate limits and credit visibility.</div>
            </div>
          </div>
        </div>
        {loading && <Spinner label="Loading..." />}
        {error && <div style={{ color: 'crimson' }}>Error: {error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {(services.filter(s => s.id !== 'api-credit')).map(s => (
            <Link key={s.id} to={`/docs/${s.id}`} className="card svc-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div style={{ fontWeight: 700 }}>{s.name}</div>
              <div className="muted" style={{ marginTop: 6 }}>{s.description || ''}</div>
            </Link>
          ))}
          {(services.filter(s => s.id !== 'api-credit')).length === 0 && !loading && <div className="muted">No services available</div>}
        </div>
      </div>
      <Footer showIndustries={false} />
    </div>
  );
}
